import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { TypeOf, z } from 'zod';

import { Card } from '@repo/ui/Card';
import { Form } from '@repo/ui/Form';
import { PaginatedSelectorInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { Controller } from 'react-hook-form';
import { useZodForm } from '@repo/hooks/useZodForm';

import GET_ROLES_FOR_ACCOUNT_Q from '../gql/getRolesForAccount.gql';
import { Mercury } from '@repo/types/Models';

// FORM SCHEMA
const changeUserRoleFormSchema = z.object({
  role: z.object({
    label: z.string(),
    value: z.string(),
  }),
});

type ChangeUserRoleFormParams = TypeOf<typeof changeUserRoleFormSchema>;

interface ChangeUserRoleProps {
  loading: boolean;
  userId: string;
  accountId: string;
  currentRole?: {
    id: string;
    name: string;
  } | null;
}

const ChangeUserRole = ({ userId, accountId, loading, currentRole }: ChangeUserRoleProps) => {
  //  GET ROLES FOR SELECT
  const PAGE_SIZE = 5;
  const [queryVars, setQueryVars] = useState<Mercury.GQL.QueryVariables | null>({
    first: PAGE_SIZE,
  });
  const {
    loading: rolesLoading,
    error,
    data,
  } = useQuery(GET_ROLES_FOR_ACCOUNT_Q, {
    variables: { ...queryVars, id: accountId },
    notifyOnNetworkStatusChange: true,
  });
  if (error) {
    console.error(error);
  }

  const roles = data?.rolesForAccount?.edges?.map((role) => {
    return {
      label: role.node.name,
      value: role.node.id,
      description: role.node.notes,
    };
  });

  const pageInfo = data?.rolesForAccount?.pageInfo;

  // Mutation
  const CHANGE_USER_ROLE = gql`
    mutation ChangeUserRole($roleId: ID!, $id: ID!) {
      changeUserRole(roleId: $roleId, id: $id) {
        user {
          id
        }
      }
    }
  `;

  const client = useApolloClient();
  const onSubmit = async (data: ChangeUserRoleFormParams) => {
    await client.mutate({
      mutation: CHANGE_USER_ROLE,
      variables: {
        roleId: data.role.value,
        id: userId,
      },
    });
  };
  const form = useZodForm({
    schema: changeUserRoleFormSchema,
    onSubmit,
    defaultValues: currentRole
      ? {
          role: {
            label: currentRole.name,
            value: currentRole.id,
          },
        }
      : undefined,
  });

  return (
    <>
      <Card title="Change user role">
        {!loading && (
          <Form {...form}>
            <Form.Success>User role successfully changed.</Form.Success>
            <Form.Error>
              Unable to change user role. Please try again later or contact support if error
              persists.
            </Form.Error>
            <Controller
              render={({ field: { onChange, value } }) => (
                <PaginatedSelectorInputField
                  loading={rolesLoading}
                  id="role"
                  label="Role"
                  onChange={onChange}
                  value={value}
                  error={form?.formState?.errors ? form?.formState?.errors['role']?.value : ''}
                  options={roles ?? []}
                  pageInfo={pageInfo}
                  setQueryVars={setQueryVars}
                  pageSize={PAGE_SIZE}
                  name="role"
                />
              )}
              control={form.control}
              name="role"
            />
            <div className="w-full">
              <Button type="submit">Update</Button>
            </div>
          </Form>
        )}
      </Card>
    </>
  );
};

export default ChangeUserRole;
